import { forwardRef, useMemo, type ReactNode } from "react";
import { SelectorAvatarStack } from "./selector-avatar";
import { SelectorPlaceholder, SelectorTrigger } from "./selector-trigger";
import type {
  SelectorAccount,
  SelectorGroup,
  SelectorTriggerProps,
} from "./types";

export interface AccountMultiSelectProps extends SelectorTriggerProps {
  accounts: SelectorAccount[];
  selectedIds: string[];
  groups?: SelectorGroup[];
  selectedGroup?: SelectorGroup;
  summary?: ReactNode;
  maxVisibleAccounts?: number;
}

export const AccountMultiSelect = forwardRef<
  HTMLButtonElement,
  AccountMultiSelectProps
>(function AccountMultiSelect(
  {
    accounts,
    groups = [],
    selectedIds,
    selectedGroup,
    summary,
    maxVisibleAccounts = 8,
    placeholder = "Select accounts",
    size = "md",
    ...props
  },
  ref
) {
  const selectedAccounts = useMemo(() => {
    const selected = new Set(selectedIds);
    return accounts.filter((account) => selected.has(account.id));
  }, [accounts, selectedIds]);

  const inferredGroup = useMemo(() => {
    if (selectedGroup || selectedIds.length === 0) return selectedGroup;
    const selected = new Set(selectedIds);
    return groups.find((group) => {
      const groupIds = (group.accounts || []).map((account) => account.id);
      return (
        groupIds.length === selected.size &&
        groupIds.every((id) => selected.has(id))
      );
    });
  }, [groups, selectedGroup, selectedIds]);

  return (
    <SelectorTrigger ref={ref} placeholder={placeholder} size={size} {...props}>
      {selectedAccounts.length === 0 ? (
        <SelectorPlaceholder>{placeholder}</SelectorPlaceholder>
      ) : (
        <span className="flex min-w-0 flex-1 items-center gap-1">
          {inferredGroup ? (
            <span className="truncate text-neutral-800">
              {inferredGroup.label}
            </span>
          ) : null}
          <SelectorAvatarStack
            accounts={selectedAccounts}
            max={
              inferredGroup
                ? Math.min(4, maxVisibleAccounts)
                : maxVisibleAccounts
            }
            size={20}
          />
          <span className="sr-only">
            {selectedAccounts.length} accounts selected
          </span>
          {summary ? <span className="ml-auto shrink-0">{summary}</span> : null}
        </span>
      )}
    </SelectorTrigger>
  );
});
